import { useMemo, useState } from 'react'
import {
  App, Button, Card, Descriptions, Drawer, Form, Input, Modal, Segmented,
  Select, Space, Statistic, Switch, Tag, Typography,
} from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { failureReasonsApi, workOrdersApi } from '@/features/maintenance/api'
import { machinesApi } from '@/features/masters/machines/api'
import { departmentsApi } from '@/features/masters/departments/api'
import { usersApi } from '@/features/users/api'
import WhatsAppShareButton from '@/shared/components/WhatsAppShareButton'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'
import type { MaintenanceWorkOrder } from '@/shared/types/maintenance'

import MaintenanceStatusBadge from './components/MaintenanceStatusBadge'
import AuditTrailDrawer from './components/AuditTrailDrawer'

const STATUS_TABS = [
  'ALL', 'REPORTED', 'ASSIGNED', 'IN_PROGRESS', 'WAITING_FOR_SPARES',
  'COMPLETED', 'CLOSED', 'REOPENED',
] as const

type StatusFilter = typeof STATUS_TABS[number]

/**
 * Spec §5 — Work Order Board.
 * Manager / Supervisor control room. Lifecycle actions are wired to the
 * backend service exactly as Phase 1 expects:
 *   ASSIGN → START → (WAIT FOR SPARES ↔ START) → COMPLETE → CLOSE / REOPEN
 * The detail drawer also exposes the Closure form (spec §14).
 */
export default function WorkOrderBoardPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL')
  const [criticalOnly, setCriticalOnly] = useState(false)
  const [selectedWO, setSelectedWO] = useState<MaintenanceWorkOrder | null>(null)
  const [auditEntityId, setAuditEntityId] = useState<string | null>(null)

  const canUpdate = usePermission('maintenance.update')

  // ── Lists ─────────────────────────────────────────────────────────────
  const { data: workOrders, isLoading } = useQuery({
    queryKey: ['work-orders', plantId, statusFilter],
    enabled: !!plantId,
    queryFn: () => workOrdersApi.list({
      plant_id: plantId,
      status: statusFilter === 'ALL' ? undefined : statusFilter,
      limit: 500,
    }),
  })

  const { data: machinesData } = useQuery({
    queryKey: ['machines-for-maint', plantId],
    enabled: !!plantId,
    queryFn: () => machinesApi.list({ plant_id: plantId, is_active: true, page: 1, page_size: 500 }),
  })
  const machines = machinesData?.items ?? []
  const machineLabel = (id: string | null) =>
    id ? machines.find((m) => m.id === id)?.machine_id_str ?? '—' : '—'

  const { data: deptsData } = useQuery({
    queryKey: ['departments-all'],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 200 }),
  })
  const depts = deptsData?.items ?? []
  const deptName = (id: string | null) =>
    id ? depts.find((d) => d.id === id)?.name ?? '—' : '—'

  const { data: failureReasons } = useQuery({
    queryKey: ['failure-reasons'],
    queryFn: () => failureReasonsApi.list(),
  })

  // ── Filtered view ─────────────────────────────────────────────────────
  const visible = useMemo(() => {
    let xs = workOrders ?? []
    if (criticalOnly) xs = xs.filter((w) => w.priority === 'CRITICAL' || w.priority === 'HIGH')
    return xs
  }, [workOrders, criticalOnly])

  // ── Stats strip ───────────────────────────────────────────────────────
  const counts = useMemo(() => {
    const w = workOrders ?? []
    return {
      open:        w.filter((x) => x.status !== 'CLOSED' && x.status !== 'COMPLETED').length,
      inProgress:  w.filter((x) => x.status === 'IN_PROGRESS').length,
      spares:      w.filter((x) => x.status === 'WAITING_FOR_SPARES').length,
      critical:    w.filter((x) => x.priority === 'CRITICAL').length,
    }
  }, [workOrders])

  // ── Lifecycle mutations ───────────────────────────────────────────────
  const ageingHours = (start: string) =>
    Math.round(dayjs().diff(dayjs(start), 'hour', true))

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['work-orders'] })
    qc.invalidateQueries({ queryKey: ['maintenance-requests'] })
  }

  const startMutation = useMutation({
    mutationFn: (id: string) => workOrdersApi.start(id),
    onSuccess: (wo) => { message.success(`WO ${wo.work_order_no} started`); setSelectedWO(wo); refresh() },
    onError: () => message.error('Could not start WO'),
  })

  // Assign technician — REPORTED → ASSIGNED
  const [assignModalOpen, setAssignModalOpen] = useState(false)
  const [assignForm] = Form.useForm()
  // Fetch active users so the supervisor can pick a technician. Filtered to
  // active-only on the server; further trade-filter could be added later.
  const { data: usersData } = useQuery({
    queryKey: ['users-for-assign'],
    queryFn: () => usersApi.list({ page: 1, page_size: 500, is_active: true }),
    staleTime: 5 * 60_000,
  })
  const assignableUsers = usersData?.items ?? []
  const assignMutation = useMutation({
    mutationFn: (vals: { assigned_to: string }) =>
      workOrdersApi.assign(selectedWO!.id, { assigned_to: vals.assigned_to }),
    onSuccess: (wo) => {
      message.success(`WO ${wo.work_order_no} assigned`)
      setAssignModalOpen(false)
      setSelectedWO(wo)
      refresh()
    },
    onError: () => message.error('Assignment failed'),
  })
  const waitMutation = useMutation({
    mutationFn: (id: string) => workOrdersApi.waitForSpares(id),
    onSuccess: (wo) => { message.success(`WO ${wo.work_order_no} marked WAITING_FOR_SPARES`); setSelectedWO(wo); refresh() },
    onError: () => message.error('Action failed'),
  })

  // Closure modal — supervisor / manager review of completed WO
  const [closeModalOpen, setCloseModalOpen] = useState(false)
  const [closeForm] = Form.useForm()
  const closeMutation = useMutation({
    mutationFn: (vals: { closure_remarks: string }) =>
      workOrdersApi.close(selectedWO!.id, vals),
    onSuccess: (wo) => {
      message.success(`WO ${wo.work_order_no} closed`)
      setCloseModalOpen(false)
      setSelectedWO(wo)
      refresh()
    },
    onError: () => message.error('Closure failed'),
  })

  // Reopen modal
  const [reopenModalOpen, setReopenModalOpen] = useState(false)
  const [reopenForm] = Form.useForm()
  const reopenMutation = useMutation({
    mutationFn: (vals: { reason: string }) => workOrdersApi.reopen(selectedWO!.id, vals),
    onSuccess: (wo) => {
      message.success(`WO ${wo.work_order_no} reopened`)
      setReopenModalOpen(false)
      setSelectedWO(wo)
      refresh()
    },
    onError: (e) => message.error((e as { message?: string })?.message ?? 'Reopen failed'),
  })

  // ── Columns ────────────────────────────────────────────────────────────
  const columns = [
    { title: 'WO No', dataIndex: 'work_order_no', key: 'work_order_no', width: 130 },
    {
      title: 'Reported', dataIndex: 'reported_datetime', key: 'rep', width: 130,
      render: (v: string) => dayjs(v).format('DD MMM HH:mm'),
    },
    {
      title: 'Machine', key: 'machine_id', width: 110,
      render: (_: unknown, r: MaintenanceWorkOrder) => machineLabel(r.machine_id),
    },
    {
      title: 'Department', key: 'dept', width: 120,
      render: (_: unknown, r: MaintenanceWorkOrder) => deptName(r.department_id),
    },
    {
      title: 'Priority', dataIndex: 'priority', key: 'priority', width: 90,
      render: (v: string) => <MaintenanceStatusBadge status={v} />,
    },
    {
      title: 'Type', dataIndex: 'maintenance_type', key: 'type', width: 110,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 160,
      render: (v: string) => <MaintenanceStatusBadge status={v} />,
    },
    {
      title: 'Ageing (h)', key: 'ageing', width: 90,
      render: (_: unknown, r: MaintenanceWorkOrder) =>
        r.status === 'CLOSED' ? '—' : ageingHours(r.reported_datetime),
    },
    {
      title: 'Downtime', key: 'downtime', width: 100,
      render: (_: unknown, r: MaintenanceWorkOrder) =>
        r.downtime_minutes != null ? `${r.downtime_minutes} min` : '—',
    },
    {
      title: 'Spare', key: 'spare', width: 70,
      render: (_: unknown, r: MaintenanceWorkOrder) =>
        r.spare_required ? <Tag color="gold">YES</Tag> : null,
    },
    {
      title: 'Actions', key: 'actions', width: 120,
      render: (_: unknown, r: MaintenanceWorkOrder) => (
        <Button size="small" type="link" onClick={() => setSelectedWO(r)}>Open</Button>
      ),
    },
  ]

  // ── Detail panel content ─────────────────────────────────────────────
  const renderDetail = (wo: MaintenanceWorkOrder) => (
    <div>
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="WO No">{wo.work_order_no}</Descriptions.Item>
        <Descriptions.Item label="Status">
          <MaintenanceStatusBadge status={wo.status} />
        </Descriptions.Item>
        <Descriptions.Item label="Priority">
          <MaintenanceStatusBadge status={wo.priority} />
        </Descriptions.Item>
        <Descriptions.Item label="Type">{wo.maintenance_type}</Descriptions.Item>
        <Descriptions.Item label="Machine">{machineLabel(wo.machine_id)}</Descriptions.Item>
        <Descriptions.Item label="Department">{deptName(wo.department_id)}</Descriptions.Item>
        <Descriptions.Item label="Reported">
          {dayjs(wo.reported_datetime).format('DD MMM YYYY HH:mm')}
        </Descriptions.Item>
        <Descriptions.Item label="Started">
          {wo.actual_start ? dayjs(wo.actual_start).format('DD MMM HH:mm') : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Completed">
          {wo.actual_end ? dayjs(wo.actual_end).format('DD MMM HH:mm') : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Approved">
          {wo.approved_at ? dayjs(wo.approved_at).format('DD MMM HH:mm') : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Response">
          {wo.response_minutes != null
            ? <Space>{wo.response_minutes} min <Tag color="geekblue">SYSTEM</Tag></Space>
            : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Repair">
          {wo.repair_minutes != null
            ? <Space>{wo.repair_minutes} min <Tag color="geekblue">SYSTEM</Tag></Space>
            : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Downtime">
          {wo.downtime_minutes != null
            ? <Space>{wo.downtime_minutes} min <Tag color="geekblue">SYSTEM</Tag></Space>
            : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Reopen count">{wo.reopen_count}</Descriptions.Item>
        <Descriptions.Item label="Action Taken" span={2}>
          {wo.action_taken ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Closure Remarks" span={2}>
          {wo.closure_remarks ?? '—'}
        </Descriptions.Item>
        {wo.reopen_reason && (
          <Descriptions.Item label="Reopen Reason" span={2}>{wo.reopen_reason}</Descriptions.Item>
        )}
      </Descriptions>

      {/* Lifecycle controls — visibility depends on status & permission */}
      {canUpdate && (
        <Space style={{ marginTop: 16 }} wrap>
          {wo.status === 'REPORTED' && (
            <Button type="primary" onClick={() => {
              assignForm.resetFields()
              setAssignModalOpen(true)
            }}>
              Assign technician
            </Button>
          )}
          {(wo.status === 'ASSIGNED' || wo.status === 'WAITING_FOR_SPARES' || wo.status === 'REOPENED') && (
            <Button type="primary" onClick={() => startMutation.mutate(wo.id)} loading={startMutation.isPending}>
              Start work
            </Button>
          )}
          {wo.status === 'IN_PROGRESS' && (
            <Button onClick={() => waitMutation.mutate(wo.id)} loading={waitMutation.isPending}>
              Mark WAITING_FOR_SPARES
            </Button>
          )}
          {wo.status === 'COMPLETED' && (
            <Button type="primary" onClick={() => {
              closeForm.resetFields()
              setCloseModalOpen(true)
            }}>
              Approve & close
            </Button>
          )}
          {wo.status === 'CLOSED' && (
            <Button danger onClick={() => {
              reopenForm.resetFields()
              setReopenModalOpen(true)
            }}>
              Reopen WO
            </Button>
          )}
          <Button onClick={() => setAuditEntityId(wo.id)}>View audit trail</Button>
          {/* Sprint 0078 — Share the WO to WhatsApp group */}
          <WhatsAppShareButton
            title={`🔧 WO ${wo.work_order_no} — ${machineLabel(wo.machine_id)}`}
            body={
              `Status: ${wo.status}\n` +
              `Priority: ${wo.priority}\n` +
              `Type: ${wo.maintenance_type}\n` +
              `Department: ${deptName(wo.department_id)}\n` +
              `Reported: ${dayjs(wo.reported_datetime).format('DD MMM HH:mm')}`
            }
            entityType="maintenance_work_orders"
            entityId={wo.id}
            buttonText="Share to WhatsApp"
            size="middle"
          />
        </Space>
      )}

      {failureReasons && wo.root_cause_id && (
        <Card size="small" style={{ marginTop: 16 }} title="Root Cause">
          {failureReasons.find((f) => f.id === wo.root_cause_id)?.reason_name ?? wo.root_cause_id}
        </Card>
      )}
    </div>
  )

  return (
    <div>
      <PageHeader
        title="Work Order Board"
        subtitle="Plan, assign, track and close all maintenance jobs."
        extra={
          <Button icon={<ReloadOutlined />} onClick={refresh}>Refresh</Button>
        }
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space size="large" wrap>
          <Statistic title="Open" value={counts.open} />
          <Statistic title="In Progress" value={counts.inProgress} />
          <Statistic title="Waiting for spares" value={counts.spares} />
          <Statistic title="Critical / High" value={counts.critical} />
        </Space>
      </Card>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Segmented
            options={STATUS_TABS as unknown as string[]}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as StatusFilter)}
          />
          <Switch checked={criticalOnly} onChange={setCriticalOnly} checkedChildren="Critical/High only" unCheckedChildren="All priorities" />
          <Typography.Text type="secondary">{visible.length} work order(s)</Typography.Text>
        </Space>
      </Card>

      <DataTable rowKey="id" columns={columns} dataSource={visible} loading={isLoading} />

      <Drawer
        title={selectedWO ? `Work Order ${selectedWO.work_order_no}` : 'Work Order'}
        open={!!selectedWO}
        onClose={() => setSelectedWO(null)}
        width={680}
        destroyOnClose
      >
        {selectedWO && renderDetail(selectedWO)}
      </Drawer>

      {/* Assign technician modal — REPORTED → ASSIGNED */}
      <Modal
        title={`Assign technician — ${selectedWO?.work_order_no ?? ''}`}
        open={assignModalOpen}
        onOk={() => assignForm.validateFields().then((v) => assignMutation.mutate(v))}
        onCancel={() => setAssignModalOpen(false)}
        confirmLoading={assignMutation.isPending}
        okText="Assign"
      >
        <Form form={assignForm} layout="vertical">
          <Form.Item
            name="assigned_to"
            label="Technician"
            rules={[{ required: true, message: 'Pick a technician to assign' }]}>
            <Select
              showSearch
              placeholder="Select technician"
              options={assignableUsers.map((u) => ({
                label: `${u.full_name ?? u.username} (${u.username})`,
                value: u.id,
              }))}
              filterOption={(input, opt) =>
                ((opt?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Closure modal — spec §14 */}
      <Modal
        title={`Approve & close ${selectedWO?.work_order_no ?? ''}`}
        open={closeModalOpen}
        onOk={() => closeForm.validateFields().then((v) => closeMutation.mutate(v))}
        onCancel={() => setCloseModalOpen(false)}
        confirmLoading={closeMutation.isPending}
      >
        <Form form={closeForm} layout="vertical">
          <Form.Item
            name="closure_remarks"
            label="Closure remarks"
            rules={[{ required: true, message: 'Closure remarks are mandatory' }]}>
            <Input.TextArea rows={3} placeholder="Trial run OK / Production restarted / etc." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Reopen modal — requires reason */}
      <Modal
        title={`Reopen ${selectedWO?.work_order_no ?? ''}`}
        open={reopenModalOpen}
        onOk={() => reopenForm.validateFields().then((v) => reopenMutation.mutate(v))}
        onCancel={() => setReopenModalOpen(false)}
        confirmLoading={reopenMutation.isPending}
      >
        <Form form={reopenForm} layout="vertical">
          <Form.Item
            name="reason" label="Reason for reopening (≥5 chars)"
            rules={[{ required: true }, { min: 5 }]}>
            <Input.TextArea rows={3} placeholder="Issue recurred — bearing seized again" />
          </Form.Item>
        </Form>
      </Modal>

      <AuditTrailDrawer
        entityId={auditEntityId}
        title="Work Order Audit Trail"
        open={auditEntityId !== null}
        onClose={() => setAuditEntityId(null)}
      />
    </div>
  )
}
